import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

// Async thunks
export const fetchPartnerData = createAsyncThunk(
  'partner/fetchPartnerData',
  async (partnerId, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            partner_id: partnerId,
            partner_name: 'Sample Partner Organization',
            mother_count: 150,
            total_savings: 2500000,
            active_contributions: 45,
            subscription_tier: 'Classic',
            subscription_status: 'active',
            created_at: '2023-01-15T00:00:00Z',
            last_activity: '2024-01-15T10:30:00Z',
            contact_info: {
              email: 'partner@example.com',
              phone: '+256 123 456 789',
              address: 'Kampala, Uganda'
            }
          });
        }, 1000);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updatePartnerProfile = createAsyncThunk(
  'partner/updatePartnerProfile',
  async (profileData, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            ...profileData,
            updated_at: new Date().toISOString()
          });
        }, 800);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPartnerStats = createAsyncThunk(
  'partner/fetchPartnerStats',
  async (partnerId, { rejectWithValue }) => {
    try {
      // Simulate API call
      const response = await new Promise((resolve) => {
        setTimeout(() => {
          resolve({
            total_mothers: 150,
            active_mothers: 120,
            total_savings: 2500000,
            monthly_contributions: 450000,
            education_funding_gap: 1800000,
            climate_active_mothers: 45,
            mothers_with_donors: 78,
            compliance_rate: 85,
            recent_activities: 12,
            pending_requests: 3
          });
        }, 600);
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  partnerData: null,
  stats: null,
  isLoading: false,
  error: null,
  lastUpdated: null,
};

const partnerSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    clearPartnerError: (state) => {
      state.error = null;
    },
    updatePartnerStats: (state, action) => {
      state.stats = { ...state.stats, ...action.payload };
    },
    setPartnerData: (state, action) => {
      state.partnerData = action.payload;
    },
    clearPartnerData: (state) => {
      state.partnerData = null;
      state.stats = null;
      state.lastUpdated = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Partner Data
      .addCase(fetchPartnerData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerData.fulfilled, (state, action) => {
        state.isLoading = false;
        state.partnerData = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchPartnerData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update Partner Profile
      .addCase(updatePartnerProfile.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updatePartnerProfile.fulfilled, (state, action) => {
        state.isLoading = false;
        state.partnerData = { ...state.partnerData, ...action.payload };
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(updatePartnerProfile.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Partner Stats
      .addCase(fetchPartnerStats.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchPartnerStats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.stats = action.payload;
        state.lastUpdated = Date.now();
        state.error = null;
      })
      .addCase(fetchPartnerStats.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearPartnerError, 
  updatePartnerStats, 
  setPartnerData, 
  clearPartnerData 
} = partnerSlice.actions;

// Selectors
export const selectPartner = (state) => state.partner;
export const selectPartnerData = (state) => state.partner.partnerData;
export const selectPartnerStats = (state) => state.partner.stats;
export const selectPartnerLoading = (state) => state.partner.isLoading;
export const selectPartnerError = (state) => state.partner.error;
export const selectPartnerName = (state) => state.partner.partnerData?.partner_name;
export const selectMotherCount = (state) => state.partner.partnerData?.mother_count;
export const selectSubscriptionTier = (state) => state.partner.partnerData?.subscription_tier;

export default partnerSlice.reducer;
